import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { format } from 'date-fns'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import axios from '../../dataClient/dataClient'
import { letterSchema } from '../../schemas/letterSchema'
import { ErrorMessage } from '../common/UserMessage'
import {
  FlexColumn,
  FlexEndButtonErrorContainer,
  FlexEndContainer,
  FlexRow,
  Form,
  OutlineContainer,
  SpaceBetweenRowContainer
} from '../styles/Containers'
import { BoldMedBodyText, MedBodyText, Subheading } from '../styles/Text'
import Button, { SecondaryButton } from '../common/Button'
import LetterForm from './LetterForm'

const LastModifiedRow = styled(FlexRow)`
  flex-wrap: wrap;
`

function formValuesFromLetter(letter) {
  return {
    name: letter?.name ?? '',
    body: letter?.body ?? ''
  }
}

function parseUpdatedAt(updatedAt) {
  const parsedDate = updatedAt instanceof Date ? updatedAt : new Date(updatedAt)
  if (Number.isNaN(parsedDate.getTime())) return null
  return {
    dateLabel: format(parsedDate, 'MMM-dd-yyyy'),
    timeLabel: format(parsedDate, 'hh:mm a')
  }
}

function EditableLetter({ letter, handleUpdateLetter = () => {}, handleCancelUpdateLetter = () => {} }) {
  const [baseline, setBaseline] = useState(() => ({
    ...formValuesFromLetter(letter),
    updatedAt: letter?.updatedAt
  }))
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState(false)

  const formMethods = useForm({
    resolver: yupResolver(letterSchema),
    defaultValues: formValuesFromLetter(letter)
  })

  const { reset, getValues, handleSubmit } = formMethods

  useEffect(() => {
    const values = formValuesFromLetter(letter)
    setBaseline({ ...values, updatedAt: letter.updatedAt })
    reset(values)
  }, [letter.id, letter.name, letter.body, letter.updatedAt, reset])

  const onError = (errors, e) => console.error('Error', errors, e)

  function handleCancel() {
    reset({ name: baseline.name, body: baseline.body })
    setSaveError(false)
    handleCancelUpdateLetter()
  }

  function onSubmit() {
    setSaveError(false)
    setIsSaving(true)
    const payload = { name: getValues('name'), body: getValues('body') }

    axios
      .patch(`/letter/${letter.id}`, payload)
      .then(({ data }) => {
        const saved = data.data
        const nextValues = formValuesFromLetter(saved)
        const nextUpdatedAt = saved.updatedAt ?? baseline.updatedAt

        setBaseline({ ...nextValues, updatedAt: nextUpdatedAt })
        reset(nextValues)
        handleUpdateLetter(saved)
      })
      .catch(e => {
        console.error('Failed to update the letter', e)
        setSaveError(true)
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  const lastModifiedParts = parseUpdatedAt(baseline.updatedAt)

  return (
    <FormProvider {...formMethods}>
      <Form onSubmit={handleSubmit(onSubmit, onError)} gap="10px">
        <FlexEndButtonErrorContainer gap="5px">
          <FlexEndContainer gap="5px">
            <SecondaryButton onClick={handleCancel} disabled={isSaving}>
              Cancel
            </SecondaryButton>
            <Button type="submit" loading={isSaving}>
              Save
            </Button>
          </FlexEndContainer>
          {saveError && <ErrorMessage>Error updating letter. Please try again.</ErrorMessage>}
        </FlexEndButtonErrorContainer>
        <OutlineContainer padding="25px 30px">
          <FlexColumn>
            <SpaceBetweenRowContainer gap="10px">
              <Subheading>Letter Draft</Subheading>
              <LastModifiedRow gap="5px">
                {lastModifiedParts ? (
                  <>
                    <BoldMedBodyText>Last Modified: {lastModifiedParts.dateLabel}</BoldMedBodyText>
                    <MedBodyText>{lastModifiedParts.timeLabel}</MedBodyText>
                  </>
                ) : (
                  <BoldMedBodyText>Last Modified: —</BoldMedBodyText>
                )}
              </LastModifiedRow>
            </SpaceBetweenRowContainer>
            <LetterForm disabled={isSaving} />
          </FlexColumn>
        </OutlineContainer>
      </Form>
    </FormProvider>
  )
}

EditableLetter.propTypes = {
  letter: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    updatedAt: PropTypes.instanceOf(Date).isRequired
  }).isRequired,
  handleUpdateLetter: PropTypes.func,
  handleCancelUpdateLetter: PropTypes.func
}

export default EditableLetter
